import { TreeSelect, TreeSelectProps } from "antd";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReimburseCategory } from "@/types/reimburse";
import { ReimburseCategoryNode, prepareReimburseTree } from "@/utils/reimburseCategory";
import { treeList } from "@/apis/reimburse";

interface ReimburseTreeSelectProps extends Omit<TreeSelectProps, 'treeData' | 'loading'> {
  value?: string;
  onChange?: (value: string) => void;
  treeData?: ReimburseCategoryNode[];
}

const ReimburseTreeSelect: FC<ReimburseTreeSelectProps> = ({
  value,
  onChange,
  treeData: suppliedTree,
  ...props
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [rawTree, setTreeData] = useState<ReimburseCategory[]>([]);

  const treeData = useMemo(() => suppliedTree ?? prepareReimburseTree(rawTree), [suppliedTree, rawTree]);

  useEffect(() => {
    if (suppliedTree === undefined) fetchTreeData();
  }, [suppliedTree]);

  const fetchTreeData = () => {
    setLoading(true);
    treeList()
      .then((res) => {
        setTreeData(res);
      })
      .catch(() => {
        setTreeData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <TreeSelect
      showSearch
      style={{ width: "100%" }}
      value={value}
      dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
      placeholder={t("reimburse.reimbursement")}
      allowClear
      loading={loading}
      treeDefaultExpandAll
      onChange={onChange}
      treeData={treeData}
      fieldNames={{
        value: "id",
        label: "label",
        children: "subs",
      }}
      treeNodeFilterProp="label"
      tagRender={(props) => {
        const { onClose, label } = props;
        return (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              maxWidth: '100%',
            }}
          >
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </span>
            {onClose && (
              <span
                style={{
                  marginLeft: 4,
                  cursor: 'pointer',
                }}
                onClick={onClose}
              >
                ×
              </span>
            )}
          </span>
        );
      }}
      {...props}
    />
  );
};

export default ReimburseTreeSelect;
